import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { styled } from '@mui/material';

import ArrowDown from '~/assets/icons/create-qr/arrow-down.svg?react';
import ArrowUp from '~/assets/icons/create-qr/arrow-up.svg?react';
import First from '~/assets/icons/create-qr/first.svg?react';
import Second from '~/assets/icons/create-qr/second.svg?react';
import Third from '~/assets/icons/create-qr/third.svg?react';
import Check from '~/assets/icons/svgs/check.svg?react';
import { Text } from '~/components/ui/text';
import { PREVIEW_BAR_VIEW_TYPE } from '~/constants/common';
import { PATH } from '~/constants/route-path';
import { useAppDispatch } from '~/hooks/useRedux';
import { qrCodeSlice } from '~/store/reducers/qr-code';
import { getQrCodeReducerData } from '~/store/reducers/qr-code/selectors';
import useBuilderStore from '~/store/useBuilderStore';
import { COLORS } from '~/styles/colors';

const icons = [
  ({ isActiveOrPast }) => {
    return <First style={{ color: isActiveOrPast ? '#2196F3' : '#6D6D6D' }} />;
  },
  ({ isActiveOrPast }) => <Second style={{ color: isActiveOrPast ? '#2196F3' : '#6D6D6D' }} />,
  ({ isActiveOrPast }) => <Third style={{ color: isActiveOrPast ? '#2196F3' : '#6D6D6D' }} />,
  ({ isActiveOrPast }) => <Third style={{ color: isActiveOrPast ? '#2196F3' : '#6D6D6D' }} />,
];

const Stepper = ({ handleStep }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const steps = [t('createQrStep1:p1'), t('createQrStep1:p2'), t('createQrStep1:p3'), t('buttons:download')];
  const { data } = useSelector(getQrCodeReducerData);

  const { setActiveStep, activeStep, setPreviewViewType } = useBuilderStore();

  const handleClickStep = (index: number) => {
    const isPastStep = activeStep > index;

    if (isPastStep) {
      if (setActiveStep) {
        setActiveStep(index);
      } else {
        handleStep(index);
      }

      if (activeStep === 1 && data.type !== 'DYNAMIC') {
        navigate(PATH.QR.CREATE);
        dispatch(qrCodeSlice.actions.resetState());
        if (setActiveStep) {
          setActiveStep(0);
        }
      }

      if (activeStep === 2 && index === 1) {
        setPreviewViewType(PREVIEW_BAR_VIEW_TYPE.PHONE);
      }
    }
  };

  return (
    <StyledContainer>
      {steps.map((step, index) => {
        const isReveredArrow = index % 2;
        const isLast = index + 1 === steps.length;
        const isActive = index === activeStep;
        const isPastStep = activeStep > index;
        return (
          <StyledItem onClick={() => handleClickStep(index)} key={index}>
            <Box sx={{ position: 'relative' }}>
              <Text
                sx={{
                  fontWeight: '600',
                  position: 'absolute',
                  color: COLORS.white,
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                }}
              >
                {isPastStep ? <Check /> : index + 1}
              </Text>
              {icons[index]({ isActiveOrPast: isActive || isPastStep })}
            </Box>
            <StyledStepText variant="t16Medium">{step}</StyledStepText>
            {!isLast && (isReveredArrow ? <ArrowDownStyled /> : <ArrowUpStyled />)}
          </StyledItem>
        );
      })}
    </StyledContainer>
  );
};

const StyledContainer = styled('div')(({ theme }) => {
  return {
    display: 'flex',
    gap: '10px',
    backgroundColor: COLORS.white,
    borderRadius: '20px',
    padding: '21px 40px',
    justifyContent: 'center',
    [theme.breakpoints.down(1100)]: {
      padding: '20px',
    },
  };
});

const StyledItem = styled('button')(() => {
  return {
    display: 'flex',
    position: 'relative',
    alignItems: 'center',
    border: 'none',
    background: 'none',
    cursor: 'pointer',
  };
});

const ArrowDownStyled = styled(ArrowDown)(() => {
  return {
    marginTop: '20px',
  };
});
const ArrowUpStyled = styled(ArrowUp)(() => {
  return {
    marginBottom: '20px',
  };
});

const StyledStepText = styled(Text)(({ theme }) => {
  return {
    marginRight: '13px',
    marginLeft: '8px',
    [theme.breakpoints.down(1100)]: {
      fontSize: '14px',
    },
  };
});

export default Stepper;
